import type { LucideIcon } from "lucide-react";
import {
  CalendarOff,
  Clock,
  Inbox,
  LogOut,
  RefreshCw,
  Settings,
  UserRound,
  Users,
  UsersRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { syncHasProblems, useSyncOverview } from "@/api/queries/sync";
import { cn } from "@/lib/cn";
import { routes } from "@/lib/routes";

export type SidebarItem = { to: string; label: string; icon: LucideIcon; end?: boolean };

export function useSidebarItems(role: "dispatcher" | "admin"): {
  main: SidebarItem[];
  admin: SidebarItem[];
} {
  const { t } = useTranslation("translation", { keyPrefix: "common.nav" });
  // Dispatchers only get the (read-only) sync page in the menu while something needs a look.
  const overview = useSyncOverview({ enabled: role === "dispatcher" });
  const main: SidebarItem[] = [
    { to: routes.dispo, label: t("inbox"), icon: Inbox, end: true },
    { to: routes.dispoTimes(), label: t("times"), icon: Clock },
    { to: routes.dispoAbsences, label: t("absences"), icon: CalendarOff },
    { to: routes.dispoEmployees, label: t("employees"), icon: UsersRound },
    ...(role === "dispatcher" && syncHasProblems(overview.data)
      ? [{ to: routes.dispoSync, label: t("sync"), icon: RefreshCw }]
      : []),
  ];
  const admin: SidebarItem[] =
    role === "admin"
      ? [
          { to: routes.adminSync, label: t("sync"), icon: RefreshCw },
          { to: routes.adminUsers, label: t("users"), icon: Users },
          { to: routes.adminSettings, label: t("settings"), icon: Settings },
        ]
      : [];
  return { main, admin };
}

export function SidebarLinks({
  items,
  collapsed,
  onNavigate,
}: {
  items: SidebarItem[];
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                "flex min-h-11 items-center gap-3 rounded-md px-3 font-medium text-sm",
                collapsed && "justify-center px-0",
                isActive ? "bg-accent-soft text-accent-strong" : "text-zinc-700 hover:bg-zinc-100",
              )
            }
          >
            <item.icon className="size-5 shrink-0" aria-hidden />
            <span className={cn(collapsed && "sr-only")}>{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// The user's name, linking to the profile (password, sessions). `collapsed` hides the name
// under `lg` like the other sidebar entries.
export function SidebarProfileLink({
  userName,
  collapsed,
  onNavigate,
}: {
  userName: string;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("translation", { keyPrefix: "common.nav" });
  return (
    <NavLink
      to={routes.profile}
      onClick={onNavigate}
      title={collapsed ? t("profile") : undefined}
      aria-label={`${t("profile")}: ${userName}`}
      className={({ isActive }) =>
        cn(
          "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm",
          collapsed && "justify-center px-0 lg:justify-start lg:px-3",
          isActive ? "bg-accent-soft text-accent-strong" : "text-zinc-700 hover:bg-zinc-100",
        )
      }
    >
      <UserRound className="size-5 shrink-0" aria-hidden />
      <span className={cn("truncate", collapsed && "hidden lg:inline")}>{userName}</span>
    </NavLink>
  );
}

// Desktop sidebar (240px, icons only under 1024px); the menu sheet on phones reuses SidebarLinks.
export function Sidebar({
  role,
  userName,
  onLogout,
}: {
  role: "dispatcher" | "admin";
  userName: string;
  onLogout: () => void;
}) {
  const { t } = useTranslation("translation", { keyPrefix: "common" });
  const { main, admin } = useSidebarItems(role);
  return (
    <aside className="sticky top-0 hidden h-dvh w-16 shrink-0 flex-col self-start border-zinc-200 border-r bg-white md:flex lg:w-60">
      <div className="flex h-14 items-center gap-2 border-zinc-200 border-b px-3 lg:px-4">
        <img src="/icon.svg" alt="" className="size-8 rounded-md" />
        <span className="hidden font-semibold lg:inline">{t("appName")}</span>
      </div>
      <nav
        aria-label={t("nav.main")}
        className="flex flex-1 flex-col gap-4 overflow-y-auto p-2 lg:p-3"
      >
        <div className="lg:hidden">
          <SidebarLinks items={[...main, ...admin]} collapsed />
        </div>
        <div className="hidden lg:block">
          <SidebarLinks items={main} />
        </div>
        {admin.length > 0 ? (
          <div className="hidden lg:block">
            <p className="px-3 pb-1 font-semibold text-xs text-zinc-500 uppercase tracking-wide">
              {t("nav.admin")}
            </p>
            <SidebarLinks items={admin} />
          </div>
        ) : null}
      </nav>
      <div className="flex flex-col gap-0.5 border-zinc-200 border-t p-2 lg:p-3">
        <SidebarProfileLink userName={userName} collapsed />
        <button
          type="button"
          onClick={onLogout}
          className="flex min-h-11 w-full items-center justify-center gap-3 rounded-md px-3 text-sm text-zinc-700 hover:bg-zinc-100 lg:justify-start"
          title={t("logout")}
        >
          <LogOut className="size-5" aria-hidden />
          <span className="hidden lg:inline">{t("logout")}</span>
        </button>
      </div>
    </aside>
  );
}
